import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Mail, Trash2, Plus, CheckCircle2, Clock, XCircle, ExternalLink, Settings, Ban } from "lucide-react";

const ROLE_OPTIONS = [
  { v: "assistant_coach", l: "Assistant Coach" },
  { v: "position_coach", l: "Position Coach" },
  { v: "offensive_coordinator", l: "Offensive Coordinator" },
  { v: "defensive_coordinator", l: "Defensive Coordinator" },
  { v: "special_teams", l: "Special Teams Coordinator" },
  { v: "strength_coach", l: "Strength Coach" },
  { v: "athletic_director", l: "Athletic Director" },
];

const STATUS_META = {
  pending: { icon: Clock, tone: "text-amber border-amber/40" },
  accepted: { icon: CheckCircle2, tone: "text-emerald-400 border-emerald-500/40" },
  revoked: { icon: XCircle, tone: "text-slate-500 border-surface-3" },
};

function StatCell({ label, value, sub }) {
  return (
    <div className="border border-surface-3 bg-surface p-4">
      <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500">{label}</div>
      <div className="font-display text-3xl text-white mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function PlanSummary({ status }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-amber" />
        <div className="font-condensed uppercase tracking-[0.25em] text-sm">Current Plan</div>
      </div>
      <div className="font-display text-4xl uppercase tracking-wide text-white mt-2">
        {status?.meta?.label || "—"}
      </div>
      <div className="text-sm text-slate-400 mt-1">{status?.meta?.price} · {status?.status?.replace("_"," ") || ""}</div>
      {status?.plan === "trial" && (
        <div className="text-xs text-amber mt-2">
          Trial ends in {status.trial_days_left} day{status.trial_days_left !== 1 ? "s" : ""}
        </div>
      )}
      {status?.period_ends_at && status?.plan !== "trial" && (
        <div className="text-xs text-slate-500 mt-2">
          Renews / expires: {new Date(status.period_ends_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function SubscriptionActions({ onPortal, onCancel, openingPortal, openingCancel }) {
  const busy = openingPortal || openingCancel;
  return (
    <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 border-t border-surface-3 pt-4" data-testid="stripe-portal-block">
      <Settings className="w-4 h-4 text-amber shrink-0 hidden sm:block" />
      <div className="flex-1">
        <div className="text-sm font-semibold">Manage Subscription</div>
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-0.5">
          Update card · Download invoices · Cancel anytime
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button onClick={onCancel} disabled={busy} data-testid="stripe-cancel-btn"
          title="Opens Stripe's secure cancellation screen"
          className="rounded-none bg-transparent hover:bg-red-500/10 text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500 h-10 text-[11px] font-condensed uppercase tracking-widest">
          <Ban className="w-3 h-3 mr-1" />
          {openingCancel ? "Opening…" : "Cancel Plan"}
        </Button>
        <Button onClick={onPortal} disabled={busy} data-testid="stripe-portal-btn"
          className="rounded-none bg-surface-2 hover:bg-surface-3 text-white border border-surface-3 hover:border-amber h-10 text-[11px] font-condensed uppercase tracking-widest">
          <ExternalLink className="w-3 h-3 mr-1" />
          {openingPortal ? "Opening…" : "Open Billing Portal"}
        </Button>
      </div>
    </div>
  );
}

function UsageGrid({ status }) {
  const usage = status?.usage || {};
  const limits = status?.free_limits || {};
  const isFree = status?.effective_plan === "free";
  const sub = (limit) => (isFree ? `Free limit: ${limit}` : "Unlimited");
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <StatCell label="AI Msgs This Month" value={usage.ai_messages_this_month ?? "—"} sub={sub(limits.ai_messages_per_month)} />
      <StatCell label="Plays" value={usage.plays ?? "—"} sub={sub(limits.plays)} />
      <StatCell label="Film Clips" value={usage.film_clips ?? "—"} sub={sub(limits.film_clips)} />
    </div>
  );
}

export function SubscriptionPanel({ status, ...actions }) {
  return (
    <div className="bg-surface border border-surface-3 p-6" data-testid="current-plan-block">
      <div className="flex items-start justify-between">
        <PlanSummary status={status} />
        <Link to="/pricing" data-testid="change-plan-btn"
          className="bg-amber hover:bg-amber-600 text-[#0B0D12] px-5 py-3 font-condensed uppercase tracking-[0.25em] text-xs transition-colors">
          Change Plan
        </Link>
      </div>
      {status?.has_stripe_subscription && <SubscriptionActions {...actions} />}
      <div className="hairline my-5" />
      <UsageGrid status={status} />
    </div>
  );
}

export function InviteForm({ form, setForm, onSubmit, sending, programLabel }) {
  return (
    <div className="p-6 border-b border-surface-3" data-testid="invite-form">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_240px_auto] gap-3">
        <Input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})}
          type="email" placeholder="coach@school.edu"
          className="bg-background border-surface-3 rounded-none" data-testid="invite-email" />
        <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
          <SelectTrigger className="bg-background border-surface-3 rounded-none" data-testid="invite-role"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-surface border-surface-3 rounded-none">
            {ROLE_OPTIONS.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={onSubmit} disabled={sending} data-testid="invite-send"
          className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-widest">
          <Plus className="w-3 h-3 mr-1" /> {sending ? "Sending…" : "Send Invite"}
        </Button>
      </div>
      <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-2">
        Invited coaches will get a link to register and join {programLabel || "your program"}.
      </div>
    </div>
  );
}

function InviteRow({ inv, onCopy, onRevoke }) {
  const meta = STATUS_META[inv.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const pending = inv.status === "pending";
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 border border-surface-3 bg-background px-4 py-3" data-testid={`invite-row-${inv.id}`}>
      <Mail className="w-4 h-4 text-slate-500" />
      <div>
        <div className="text-sm font-semibold">{inv.email}</div>
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">
          {inv.role.replace(/_/g, " ")} · sent {new Date(inv.created_at).toLocaleDateString()}
        </div>
      </div>
      <span className={`text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border ${meta.tone} flex items-center gap-1`}>
        <Icon className="w-3 h-3" /> {inv.status}
      </span>
      {pending && (
        <button onClick={() => onCopy(inv)} data-testid={`invite-copy-${inv.id}`}
          className="text-[10px] font-condensed uppercase tracking-widest text-amber hover:underline flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> Copy link
        </button>
      )}
      {pending && (
        <button onClick={() => onRevoke(inv)} data-testid={`invite-revoke-${inv.id}`}
          className="text-slate-500 hover:text-red-400">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function InviteList({ invites, loading, onCopy, onRevoke }) {
  return (
    <div className="p-6">
      <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mb-3">
        {invites.length} invite{invites.length !== 1 ? "s" : ""}
      </div>
      {loading && <div className="text-sm text-slate-400">Loading…</div>}
      {!loading && invites.length === 0 && (
        <div className="text-sm text-slate-500 italic">No invites yet.</div>
      )}
      <div className="space-y-2">
        {invites.map(inv => <InviteRow key={inv.id} inv={inv} onCopy={onCopy} onRevoke={onRevoke} />)}
      </div>
    </div>
  );
}
